
import React, { useCallback, useEffect, useState } from "react";
import Loading from "../../Common/Loading";

interface MDRecord {
    found: boolean;
    message?: string;
    schoolCode?: string;
    familyCode?: string;
    login?: string;
    setByTeacher?: string;
    mdStatus?: string;
    reportNo?: number;
}

interface ResetResponse {
    success?: boolean;
    message?: string;
}

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

const ResetMD: React.FC = () => {
    const [record, setRecord] = useState<MDRecord | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [alertMsg, setAlertMsg] = useState<string | null>(null);
    const [resetting, setResetting] = useState<boolean>(false);

    // Get query parameters
    const getParam = (name: string): string => {
        return new URLSearchParams(window.location.search).get(name) ?? "";
    };

    const schoolCode = getParam("SchoolCode");
    const familyCode = getParam("FamilyCode");
    const login = getParam("Login");

    /**
     * Fetch Maths Doctor record
     */
    const fetchRecord = useCallback(async () => {
        setLoading(true);
        setAlertMsg(null);

        try {
            if (!API_BASE_URL) {
                throw new Error("API base URL is not configured.");
            }

            if (!schoolCode || !familyCode || !login) {
                setRecord(null);
                setAlertMsg(
                    "SchoolCode, FamilyCode and Login are required."
                );
                return;
            }

            const params = new URLSearchParams({
                C: schoolCode,
                F: familyCode,
                L: login,
            });

            const response = await fetch(
                `${API_BASE_URL}/api/reset-md/?${params.toString()}`,
                {
                    method: "GET",
                    cache: "no-store",
                    credentials: "include",
                }
            );

            if (!response.ok) {
                throw new Error(
                    `Failed to fetch record. Status: ${response.status}`
                );
            }

            const data: MDRecord = await response.json();

            setRecord(data);

            if (!data.found) {
                setAlertMsg(data.message || "No Maths Doctor record found.");
            }
        } catch (error) {
            console.error("Error fetching Maths Doctor record:", error);

            setRecord(null);
            setAlertMsg("Failed to load record.");
        } finally {
            setLoading(false);
        }
    }, [schoolCode, familyCode, login]);

    /**
     * Load record when component mounts
     */
    useEffect(() => {
        fetchRecord();
    }, [fetchRecord]);

    /**
     * Reset Maths Doctor
     */
    const handleReset = async () => {
        if (!record?.found) {
            return;
        }

        // Do not allow reset if MD is already completed
        if (
            record.mdStatus?.trim().toLowerCase() ===
            "md completed"
        ) {
            setAlertMsg("Cannot reset completed MD.");
            return;
        }

        if (!API_BASE_URL) {
            setAlertMsg("API base URL is not configured.");
            return;
        }

        setResetting(true);
        setAlertMsg(null);

        try {
            const resetPayload = {
                schoolCode: record.schoolCode,
                familyCode: record.familyCode,
                login: record.login,
                reportNo: record.reportNo,
                setByTeacher: record.setByTeacher,
                mdStatus: record.mdStatus
            };

            const response = await fetch(
                `${API_BASE_URL}/api/reset-md/reset/`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                    body: JSON.stringify(resetPayload),
                }
            );

            if (!response.ok) {
                throw new Error(
                    `Reset failed. Status: ${response.status}`
                );
            }

            const data: ResetResponse = await response.json();

            if (data.success) {
                const message =
                    data.message || "Maths Doctor reset successfully.";

                alert(message);
                setAlertMsg(message);

                // Reload the record after successful reset
                await fetchRecord();
            } else {
                setAlertMsg(
                    data.message || "Failed to reset Maths Doctor."
                );
            }
        } catch (error) {
            console.error("Error resetting Maths Doctor:", error);
            setAlertMsg("Error resetting Maths Doctor.");
        } finally {
            setResetting(false);
        }
    };

    /**
     * Close window
     */
    const handleClose = () => {
        window.close();
    };

    /**
     * Alert message color
     */
    const isSuccessMessage =
        alertMsg?.toLowerCase().includes("successfully") ||
        alertMsg?.toLowerCase().includes("success");

    return (
        <div
            style={{
                fontFamily: "Times New Roman",
                fontSize: "16px",
            }}
        >
            <div
                style={{
                    width: "40%",
                    padding: "10px 40px",
                    backgroundColor: "#f2f2f2",
                }}
            >
                <h1
                    style={{
                        fontSize: "1em",
                        textAlign: "center",
                        fontFamily: "Arial",
                    }}
                >
                    Reset Maths Doctor
                </h1>

                {alertMsg && (
                    <div
                        style={{
                            marginBottom: "10px",
                            color: isSuccessMessage ? "green" : "red",
                            fontWeight: "bold",
                        }}
                    >
                        {alertMsg}
                    </div>
                )}

                {loading ? (
                    <Loading />
                ) : (
                    <>
                        {record?.found && (
                            <>
                                {[
                                    {
                                        label: "School Code",
                                        value: record.schoolCode,
                                    },
                                    {
                                        label: "Family",
                                        value: record.familyCode,
                                    },
                                    {
                                        label: "Login",
                                        value: record.login,
                                    },
                                    {
                                        label: "Set By Teacher/Tutor?",
                                        value: record.setByTeacher,
                                    },
                                    {
                                        label: "MD Status",
                                        value: record.mdStatus,
                                    },
                                ].map((field, index) => (
                                    <div
                                        key={`${field.label}-${index}`}
                                        style={{
                                            borderRadius: "5px",
                                            padding: "6px 20px",
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        <label
                                            style={{
                                                minWidth: "180px",
                                            }}
                                        >
                                            {field.label}
                                        </label>

                                        <input
                                            type="text"
                                            value={field.value ?? ""}
                                            readOnly
                                            style={{
                                                width: "60%",
                                                padding: "8px 10px",
                                                margin: "0",
                                                border: "1px solid #ccc",
                                                backgroundColor: "#f2f2f2",
                                                borderRadius: "4px",
                                                fontSize: "12pt",
                                            }}
                                        />
                                    </div>
                                ))}

                                <div
                                    style={{
                                        borderRadius: "5px",
                                        padding: "10px 0 10px 60px",
                                        display: "flex",
                                        gap: "8px",
                                    }}
                                >
                                    <button
                                        type="button"
                                        onClick={handleReset}
                                        disabled={
                                            resetting ||
                                            record.mdStatus
                                                ?.trim()
                                                .toLowerCase() ===
                                            "md completed"
                                        }
                                        style={{
                                            width: "40%",
                                            backgroundColor:
                                                resetting ||
                                                    record.mdStatus
                                                        ?.trim()
                                                        .toLowerCase() ===
                                                    "md completed"
                                                    ? "#999"
                                                    : "#4CAF50",
                                            color: "white",
                                            padding: "14px 30px",
                                            border: "none",
                                            borderRadius: "4px",
                                            cursor:
                                                resetting ||
                                                    record.mdStatus
                                                        ?.trim()
                                                        .toLowerCase() ===
                                                    "md completed"
                                                    ? "not-allowed"
                                                    : "pointer",
                                        }}
                                    >
                                        {resetting
                                            ? "Resetting..."
                                            : "RESET"}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={handleClose}
                                        style={{
                                            width: "40%",
                                            backgroundColor: "#4CAF50",
                                            color: "white",
                                            padding: "14px 30px",
                                            border: "none",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                        }}
                                    >
                                        Close
                                    </button>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ResetMD;

